import { useEffect, useRef } from 'react'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Smile } from 'lucide-react'
import { useRecentEmojis, RecentEmoji } from '@/hooks/useRecentEmojis'

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void
  disabled?: boolean
}

interface EmojiData {
  id: string
  name: string
  native: string
  unified: string
  keywords?: string[]
  shortcodes?: string
  skin?: number
}

export function EmojiPicker({ onEmojiSelect, disabled }: EmojiPickerProps) {
  const { recentEmojis, addRecentEmoji } = useRecentEmojis()
  const pickerRef = useRef<HTMLDivElement>(null)

  const handleEmojiSelect = (emojiData: EmojiData) => {
    const emoji: RecentEmoji = {
      id: emojiData.id,
      native: emojiData.native,
      unified: emojiData.unified,
    }

    // Add to recent emojis
    addRecentEmoji(emoji)

    // Pass the native emoji to the parent
    onEmojiSelect(emojiData.native)
  }

  // Convert our recent emojis to emoji-mart format
  const customCategories = recentEmojis.length > 0 ? [
    {
      id: 'recent',
      name: 'Recent',
      emojis: recentEmojis.map(e => e.id),
    }
  ] : undefined

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          disabled={disabled}
          type="button"
        >
          <Smile className="mr-1 h-4 w-4" />
          Emoji
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 border-0 shadow-lg"
        align="start"
        side="top"
      >
        <div ref={pickerRef}>
          <Picker
            data={data}
            onEmojiSelect={handleEmojiSelect}
            theme="dark"
            previewPosition="none"
            skinTonePosition="search"
            searchPosition="sticky"
            categories={[
              'frequent',
              'people',
              'nature',
              'foods',
              'activity',
              'places',
              'objects',
              'symbols',
              'flags',
            ]}
            custom={customCategories}
            set="native"
            perLine={8}
            maxFrequentRows={2}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
